import React, { useEffect, useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import {
  useGoogleSheetsStore,
  selectIsDirty,
  COLUMN_POSITIONS,
  FIELD_LABELS,
} from "../store/useGoogleSheetsStore";
import { useShopifySaveBar } from "../hooks/useShopifySaveBar";

// ---------- Field label helper ----------

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

// ---------- Not-connected view ----------

const NotConnectedView: React.FC = () => {
  const connectGoogle = useGoogleSheetsStore((s) => s.connectGoogle);

  return (
    <s-page heading="Google Sheets">
      <s-section>
        <s-box
          background="base"
          border="small subdued solid"
          borderRadius="large-100"
          padding="large-400"
        >
          <s-stack direction="block" alignItems="center" gap="large-200">
            <s-text type="strong">
              Importa tus pedidos automáticamente en Google Sheets
            </s-text>
            <s-text color="subdued">
              Conecta tu cuenta de Google para comenzar a exportar pedidos a una
              hoja de cálculo de forma automática.
            </s-text>
            <s-button variant="primary" onClick={() => void connectGoogle()}>
              Conectar Google Sheets
            </s-button>
            <s-text color="subdued">
              Después de conectar, haz clic en el botón para refrescar la
              página. ¿Necesitas ayuda? ¡Contáctanos!
            </s-text>
            <s-stack direction="inline" gap="base">
              <s-button icon="refresh" onClick={() => window.location.reload()}>
                Refrescar página
              </s-button>
              <s-button icon="chat">Contáctanos</s-button>
            </s-stack>
          </s-stack>
        </s-box>
      </s-section>
    </s-page>
  );
};

// ---------- Column mapping row ----------

const ColumnMappingRow: React.FC<{ availableFields: string[] }> = ({
  availableFields,
}) => {
  const columnMapping = useGoogleSheetsStore((s) => s.columnMapping);
  const setColumnField = useGoogleSheetsStore((s) => s.setColumnField);

  return (
    <div>
      {COLUMN_POSITIONS.map((col) => (
        <div
          key={col}
          style={{
            display: "grid",
            gridTemplateColumns: "40px 1fr",
            alignItems: "center",
            gap: "12px",
            padding: "6px 0",
            borderBottom: "1px solid #e1e3e5",
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: "14px",
              color: "#6d7175",
              textAlign: "center",
            }}
          >
            {col}
          </span>
          <s-select
            value={columnMapping[col] ?? ""}
            onChange={(e: CallbackEvent<"s-select">) =>
              setColumnField(col, e.currentTarget.value)
            }
          >
            <s-option value="">Seleccionar campo</s-option>
            {availableFields.map((field) => (
              <s-option key={field} value={field}>
                {fieldLabel(field)}
              </s-option>
            ))}
          </s-select>
        </div>
      ))}
    </div>
  );
};

// ---------- Connected view ----------

const ConnectedView: React.FC = () => {
  const [helpBannerDismissed, setHelpBannerDismissed] = useState(false);
  const isEnabled = useGoogleSheetsStore((s) => s.isEnabled);
  const spreadsheetId = useGoogleSheetsStore((s) => s.spreadsheetId);
  const sheetName = useGoogleSheetsStore((s) => s.sheetName);
  const exportMode = useGoogleSheetsStore((s) => s.exportMode);
  const availableFiles = useGoogleSheetsStore((s) => s.availableFiles);
  const availableSheets = useGoogleSheetsStore((s) => s.availableSheets);
  const availableFields = useGoogleSheetsStore((s) => s.availableFields);
  const isLoadingFiles = useGoogleSheetsStore((s) => s.isLoadingFiles);
  const isLoadingSheets = useGoogleSheetsStore((s) => s.isLoadingSheets);
  const isDisconnecting = useGoogleSheetsStore((s) => s.isDisconnecting);

  const setEnabled = useGoogleSheetsStore((s) => s.setEnabled);
  const selectSpreadsheet = useGoogleSheetsStore((s) => s.selectSpreadsheet);
  const selectSheet = useGoogleSheetsStore((s) => s.selectSheet);
  const includeHeaders = useGoogleSheetsStore((s) => s.includeHeaders);
  const setExportMode = useGoogleSheetsStore((s) => s.setExportMode);
  const setIncludeHeaders = useGoogleSheetsStore((s) => s.setIncludeHeaders);
  const refreshFiles = useGoogleSheetsStore((s) => s.refreshFiles);
  const refreshSheets = useGoogleSheetsStore((s) => s.refreshSheets);
  const disconnect = useGoogleSheetsStore((s) => s.disconnect);

  const handleDisconnect = async () => {
    if (
      !confirm("¿Estás seguro de que deseas desconectar tu cuenta de Google?")
    )
      return;
    try {
      await disconnect();
      window.shopify?.toast?.show("Cuenta de Google desconectada");
    } catch {
      window.shopify?.toast?.show("Error al desconectar", { isError: true });
    }
  };

  const handleSpreadsheetChange = (e: CallbackEvent<"s-select">) => {
    const selectedId = e.currentTarget.value;
    const file = availableFiles.find((f) => f.id === selectedId);
    if (file) {
      void selectSpreadsheet(file.id, file.name);
    } else {
      void selectSpreadsheet("", "");
    }
  };

  const handleSheetChange = (e: CallbackEvent<"s-select">) => {
    const selectedTitle = e.currentTarget.value;
    const sheet = availableSheets.find((s) => s.title === selectedTitle);
    if (sheet) {
      selectSheet(String(sheet.sheetId), sheet.title);
    } else {
      selectSheet("", "");
    }
  };

  // The "Open in Google Sheets" URL
  const sheetsUrl = spreadsheetId
    ? `https://docs.google.com/spreadsheets/d/${spreadsheetId}`
    : null;

  return (
    <s-page heading="Google Sheets">
      {/* Top actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "var(--s-space-large-200) 0 24px",
        }}
      >
        <h1 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>
          Google Sheets
        </h1>
        <s-stack direction="inline" gap="base">
          {sheetsUrl && (
            <s-button
              icon="external"
              onClick={() => window.open(sheetsUrl, "_blank")}
            >
              Abrir archivo de Google Sheets
            </s-button>
          )}
          <s-button
            variant="primary"
            tone="critical"
            loading={isDisconnecting}
            onClick={() => void handleDisconnect()}
          >
            Desconectar cuenta de Google
          </s-button>
        </s-stack>
      </div>

      {/* Step 1 */}
      <s-section>
        <s-stack direction="block" gap="large-200">
          <s-text type="strong">
            1. Selecciona la hoja de cálculo de Google donde se importarán tus
            pedidos
          </s-text>

          <s-divider />

          {/* Enable auto-export */}
          <s-checkbox
            label="Habilita la importación automática de tus pedidos en hojas de cálculo de Google"
            checked={isEnabled}
            onChange={(e: CallbackEvent<"s-checkbox">) =>
              setEnabled(e.currentTarget.checked)
            }
          />

          <s-divider />

          {/* Spreadsheet selector */}
          <s-stack direction="block" gap="small-200">
            <s-text type="strong">
              Selecciona tu archivo de hoja de cálculo
            </s-text>
            <s-stack direction="inline" gap="base" alignItems="end">
              <s-select
                value={spreadsheetId}
                placeholder={
                  isLoadingFiles
                    ? "Cargando archivos..."
                    : "Selecciona tu archivo de hoja de cálculo"
                }
                onChange={handleSpreadsheetChange}
                disabled={isLoadingFiles}
              >
                {availableFiles.map((file) => (
                  <s-option key={file.id} value={file.id}>
                    {file.name}
                  </s-option>
                ))}
              </s-select>
              <s-button
                icon="refresh"
                loading={isLoadingFiles}
                onClick={() => void refreshFiles()}
              >
                Actualizar
              </s-button>
            </s-stack>
          </s-stack>

          <s-divider />

          {/* Sheet (tab) selector */}
          <s-stack direction="block" gap="small-200">
            <s-text color="subdued">Selecciona tu hoja</s-text>
            <s-stack direction="inline" gap="base" alignItems="end">
              <s-select
                value={sheetName}
                placeholder={
                  isLoadingSheets ? "Cargando hojas..." : "Selecciona tu hoja"
                }
                disabled={!spreadsheetId || isLoadingSheets}
                onChange={handleSheetChange}
              >
                {availableSheets.map((sheet) => (
                  <s-option key={sheet.sheetId} value={sheet.title}>
                    {sheet.title}
                  </s-option>
                ))}
              </s-select>
              <s-button
                icon="refresh"
                disabled={!spreadsheetId}
                loading={isLoadingSheets}
                onClick={() => void refreshSheets()}
              >
                Actualizar
              </s-button>
            </s-stack>
          </s-stack>

          <s-divider />

          {/* Export mode */}
          <s-stack direction="block" gap="small-200">
            <s-text type="strong">
              Selecciona qué tipos de pedidos importar
            </s-text>
            <s-select
              value={exportMode}
              onChange={(e: CallbackEvent<"s-select">) =>
                setExportMode(
                  e.currentTarget.value as "ALL" | "ORDERS_ONLY" | "LEADS_ONLY"
                )
              }
            >
              <s-option value="ALL">Todos los pedidos</s-option>
              <s-option value="ORDERS_ONLY">Sólo pedidos normales</s-option>
              <s-option value="LEADS_ONLY">Sólo pedidos abandonados</s-option>
            </s-select>
          </s-stack>

          <s-divider />

          {/* Include headers toggle */}
          <s-checkbox
            label="Incluir fila de encabezados en la hoja de cálculo"
            checked={includeHeaders}
            onChange={(e: CallbackEvent<"s-checkbox">) =>
              setIncludeHeaders(e.currentTarget.checked)
            }
          />

          <s-divider />

          {/* Info banners */}
          <s-banner tone="info" dismissible>
            Si no tiene un archivo de Google Sheets para tus pedidos COD, créelo
            en tu cuenta de Google Drive y luego haga clic en el botón{" "}
            <strong>Actualizar</strong>.
          </s-banner>

          <s-banner tone="info" dismissible>
            Solo se importarán los pedidos nuevos en su archivo de Google
            Sheets.
          </s-banner>
        </s-stack>
      </s-section>

      {/* Step 2: Column mapping */}
      <s-section>
        <s-stack direction="block" gap="large-200">
          <s-text type="strong">2. Configura los campos de la columna</s-text>

          <s-divider />

          {/* Sheet Layout Design label */}
          <s-stack direction="inline" gap="base" alignItems="center">
            <s-text type="strong">Sheet Layout Design</s-text>
            <s-badge tone="info">Standard</s-badge>
          </s-stack>

          <s-text color="subdued">
            Asigna cada columna de tu hoja de cálculo a un campo del pedido. Las
            columnas sin campo asignado serán ignoradas.
          </s-text>

          <s-divider />

          {/* Column Mapping table */}
          <s-stack direction="block" gap="small-200">
            <s-text type="strong">Column Mapping</s-text>
            <ColumnMappingRow availableFields={availableFields} />
          </s-stack>
        </s-stack>
      </s-section>

      {/* Help banner */}
      {!helpBannerDismissed && (
        <s-section>
          <s-banner
            tone="info"
            dismissible
            onAfterHide={() => setHelpBannerDismissed(true)}
          >
            <s-stack direction="block" gap="base">
              <s-text>
                ¿Necesitas ayuda con nuestra integración de{" "}
                <strong>Google Sheets</strong>? ¡Contáctanos, nuestros técnicos
                estarán encantados de ayudarte!
              </s-text>
              <s-button icon="chat">Contáctanos</s-button>
            </s-stack>
          </s-banner>
        </s-section>
      )}
    </s-page>
  );
};

// ---------- Main page ----------

const IntegrationSettingsPage: React.FC = () => {
  const initialize = useGoogleSheetsStore((s) => s.initialize);
  const isConnected = useGoogleSheetsStore((s) => s.isConnected);
  const isLoading = useGoogleSheetsStore((s) => s.isLoading);
  const error = useGoogleSheetsStore((s) => s.error);
  const isDirty = useGoogleSheetsStore(selectIsDirty);
  const isSaving = useGoogleSheetsStore((s) => s.isSaving);
  const save = useGoogleSheetsStore((s) => s.save);
  const discard = useGoogleSheetsStore((s) => s.discard);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  useShopifySaveBar({ isDirty, isSaving, onSave: save, onDiscard: discard });

  if (isLoading) {
    return (
      <div>
        <s-page heading="Google Sheets">
          <s-box padding="large-400">
            <s-stack direction="block" gap="base" alignItems="center">
              <s-spinner />
              <s-text color="subdued">Cargando configuración...</s-text>
            </s-stack>
          </s-box>
        </s-page>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <s-page heading="Google Sheets">
          <s-banner tone="critical">
            <s-text>Error al cargar: {error}</s-text>
          </s-banner>
        </s-page>
      </div>
    );
  }

  return <div>{isConnected ? <ConnectedView /> : <NotConnectedView />}</div>;
};

export default IntegrationSettingsPage;
